export type OutageAssetScope = { all: boolean; assetIds: number[] };

/** Parses the /outage-metadata/assets payload. Throws on a bad shape, and on
 * {all: false, asset_ids: []} (no usable entry), so callers treat both as a failed call. */
export function parseOutageAssetScope(data: unknown): OutageAssetScope {
  const d = data as { all?: unknown; asset_ids?: unknown } | null;
  if (!d || typeof d !== "object" || typeof d.all !== "boolean" || !Array.isArray(d.asset_ids)) {
    throw new Error("Malformed outage asset scope");
  }
  if (!d.asset_ids.every((id) => typeof id === "number" && Number.isInteger(id))) {
    throw new Error("Malformed outage asset scope ids");
  }
  if (!d.all && d.asset_ids.length === 0) {
    throw new Error("Outage asset scope is empty");
  }
  return { all: d.all, assetIds: d.asset_ids as number[] };
}

/** Keeps assets the scope allows; keepAssetId (the asset of an outage being edited) always stays. */
export function filterAssetsByScope<T extends { asset_id: number }>(
  assets: T[],
  scope: OutageAssetScope,
  keepAssetId?: number
): T[] {
  if (scope.all) return assets;
  const allowed = new Set(scope.assetIds);
  return assets.filter((a) => allowed.has(a.asset_id) || a.asset_id === keepAssetId);
}

/**
 * View mode only displays an outage's own asset, so it never waits for, or
 * fails on, the scope call. Create and edit need the real scope.
 */
export function scopeForForm(
  scope: OutageAssetScope | null,
  viewOnly: boolean
): OutageAssetScope | null {
  return viewOnly ? { all: true, assetIds: [] } : scope;
}
